import type { Styles } from '@adornis/ass/style.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { TextMultipleLangItem } from '@adornis/buchungsheld-db/entities/TextMultipleLangItem.js';
import type { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import { WorkshopSet } from '@adornis/buchungsheld-db/entities/WorkshopSet.js';
import { isDefined } from '@adornis/buchungsheld-db/util.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { ComponentSizeController } from '@adornis/chemistry/controllers/component-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design.js';
import { useContext } from '@adornis/functional-lit/directive-provide-context.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { clientTranslator } from '@adornis/translation-core/client/clientTranslator.js';
import { createContext, provide } from '@lit/context';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { chooseValueByLang, renderProductCardTags, type ProductCardData } from './productCardShared.js';
import { workshopCardParts, type WorkshopCardData } from './renderWorkshopCard.js';

type SubWorkshop = {
  _id: 1;
  name: 1;
  institutionID: 1;
  settings: { courseLocation: 1; inheritedCourseLocation: 1 };
  language: 1;
  presenceType: 1;
  topic: 1;
  productEntryRequirementLabels: 1;
  appointments: {
    start: 1;
    end: 1;
    name: 1;
    location: 1;
  };
};

export type WorkshopSetCardData = ProductCardData & {
  workshopSet: NarrowedEntity<
    WorkshopSet,
    {
      _id: 1;
      name: 1;
      institutionID: 1;
      translatedDescriptionHTML: { langKey: 1; text: 1 };
      categoryID: 1;
      category: { _id: 1; name: 1 };
      workshops: SubWorkshop;
    }
  >;
  workshops: Array<WorkshopCardData>;
  selected: boolean;
  isClickable: boolean;
  events?: {
    onShowDetailPage?: (workshop: NarrowedEntity<Workshop, SubWorkshop>) => void;
    onWorkshopSelect?: (workshop: NarrowedEntity<Workshop, SubWorkshop>) => void;
  };
};

export const workshopSetCardDataContext = createContext<WorkshopSetCardData>('workshop-set-card-data');
const IsMobileViewEnabledContext = createContext<boolean>('is-mobile-view-enabled');

@customElement('bh-workshop-set-card')
export class BHWorkshopSetCard extends ChemistryLitElement {
  @state()
  workshopSetCardData?: WorkshopSetCardData;

  @provide({ context: IsMobileViewEnabledContext })
  @state()
  isMobileViewEnabled = true;

  @state() private readonly size = new ComponentSizeController(this);

  override render() {
    this.isMobileViewEnabled = (this.size.width ?? 0) < 768;
    return html` <slot></slot> `;
  }

  override styles(): Styles[] {
    const accentColor = this.workshopSetCardData?.colorDef.stepColor;
    const shadow = '0 1px 3px 0 #d4d4d5, 0 0 0 1px #d4d4d5';
    const selectedShadow = `0 1px 3px 0 ${accentColor}, 0 0 0 1px ${accentColor}`;
    const raisedShadow = '0 0 0 1px #d4d4d5, 0 2px 4px 0 rgba(34,36,38,.12), 0 2px 10px 0 rgba(34,36,38,.15)';
    const hoverShadow = '0 1px 3px 0 #bcbdbd, 0 0 0 1px #d4d4d5';
    const hoverStyle = { transform: 'translateY(-2px)', boxShadow: hoverShadow };

    return [
      ...super.styles(),
      {
        ':host': {
          display: 'flex',
          borderRadius: this.sizes.borderRadius,
          boxShadow: this.workshopSetCardData?.selected ? selectedShadow : shadow,
          overflow: 'hidden',
          boxSizing: 'border-box',
          background: this.workshopSetCardData?.selected
            ? `rgb(from ${this.workshopSetCardData.colorDef.stepColor} r g b / 0.1)`
            : this.colors.white,
          position: 'relative',
          padding: '20px',
          userSelect: 'none',
          cursor: this.workshopSetCardData?.isClickable ? 'pointer' : 'not-allowed',
          '&[raised]': { boxShadow: raisedShadow },
          '&:hover': { '&[href]': hoverStyle },
          '@media print': { boxShadow: 'none', border: `1px solid ${this.colors.neutral.shade(200)}` },
        },
      },
    ];
  }
}

export const workshopSetCardDescription = stateful<WorkshopSetCardData>(({ workshopSet }) => {
  return html`
    <bh-expandable-content
      @click=${event => {
        if (event.target.tagName === 'TR-LINK') {
          // do not select card when user clicked on Link
          event.stopPropagation();
        }
      }}
    >
      <tr-flex ${css({ gap: '1em' })}>
        ${chooseValueByLang(
          // TODO: fix types
          TextMultipleLangItem.mapAllTranslationsByLang(workshopSet.translatedDescriptionHTML as any),
        )}
      </tr-flex>
    </bh-expandable-content>
  `;
});

const workshopSetCardWorkshops = stateful<WorkshopSetCardData>(({ workshops, colorDef, events }) => {
  const theme = useObservable(() => DesignSystem.currentTheme, []);
  const showMobileView = useContext(IsMobileViewEnabledContext);

  if (!theme) throw new Error('theme is undefined');

  return html`
    <x-flex vertical space="md">
      ${workshops.map(workshopData => {
        const { selected, isClickable } = workshopData;

        const dateBoxes = html`
          <x-flex vertical space="xs" ${css({ height: 'min-content', gridArea: 'date' })}>
            ${workshopCardParts.workshopCardTerminButton(workshopData)}
            ${workshopCardParts.workshopCapacityBar(workshopData)}
          </x-flex>
        `;

        const heading = html`
          <x-h3
            ${css({
              gridArea: showMobileView ? 'heading' : 'content',
              alignSelf: 'center',
              width: '100%',
              margin: '10px 0',
            })}
            >${workshopData.workshop.name}</x-h3
          >
        `;

        const detailButton = html`
          <x-flex
            horizontal
            crossaxis-end
            ${css({
              gridArea: showMobileView ? 'details' : 'content',
              position: showMobileView ? 'static' : 'absolute',
              justifyContent: 'end',
              right: '0',
              bottom: '0',
            })}
          >
            ${workshopCardParts.workshopCardDetailPageButton(workshopData)}
          </x-flex>
        `;

        const tags = html`<x-flex ${css({ gridArea: 'tags' })} horizontal>
          ${workshopCardParts.workshopCardTags(workshopData)}
        </x-flex>`;

        const indicator = selected
          ? html`
              <tr-icon
                ${css({
                  position: 'absolute',
                  right: '-10px',
                  top: '-10px',
                  background: colorDef.stepColor,
                  borderColor: colorDef.stepColor,
                  borderRadius: theme.sizes.borderRadius,
                  padding: theme.spacing.xxs,
                  color: '#fff',
                })}
                small
              >
                task_alt
              </tr-icon>
            `
          : nothing;

        const content = showMobileView
          ? [dateBoxes, heading, workshopCardParts.workshopCardDescription(workshopData), detailButton, tags]
          : html`${dateBoxes}
              <x-flex vertical space="md" ${css({ height: 'min-content', gridArea: 'content' })}
                >${heading} ${workshopCardParts.workshopCardDescription(workshopData)} ${detailButton}</x-flex
              >
              ${tags}`;

        return html`
          <x-grid
            ${css({
              width: '95%',
              gridTemplateColumns: showMobileView ? '1fr' : '30fr 70fr',
              gridTemplateAreas: showMobileView
                ? '"heading" "date" "content" "tags" "details"'
                : '"date content" "tags tags"',
              gap: '10px',
              padding: theme.spacing.sm,
              borderRadius: theme.sizes.borderRadius,
              border: selected ? `1px solid ${colorDef.stepColor}` : `1px solid ${theme.colors.tone.black}`,
              cursor: isClickable ? 'pointer' : 'not-allowed',
              position: 'relative',
              opacity: !isClickable ? '0.5' : '1',
              background: selected ? `rgb(from ${colorDef.stepColor} r g b / 0.1)` : 'white',
            })}
            @click=${(e: MouseEvent) => {
              e.stopPropagation();
              if (isClickable) events?.onWorkshopSelect?.(workshopData.workshop);
            }}
          >
            ${content} ${indicator}
          </x-grid>
        `;
      })}
    </x-flex>
  `;
});

export const workshopSetCardTags = stateful<WorkshopSetCardData>(({ workshopSet }) => {
  const theme = useObservable(() => DesignSystem.currentTheme, []);
  const translate = clientTranslator.translate.bind(clientTranslator);

  if (!theme) throw new Error('theme is undefined');

  const tagsIterator = (function* () {
    const languagesSet = workshopSet.workshops
      ?.map(workshop => workshop.language)
      .filter(isDefined)
      .reduce((acc, lang) => (acc.add(lang), acc), new Set<string>());
    const languages = languagesSet ? Array.from(languagesSet) : [];
    if (languages.length > 0)
      yield { icon: 'language', label: languages.map(l => clientTranslator.translate('languages', l)).join(', ') };

    const presenceTypesSet = new Set(workshopSet.workshops?.map(workshop => workshop.presenceType));
    const presenceTypes = Array.from(presenceTypesSet).filter(p => p !== 'not_selected');

    if (presenceTypes.length > 0)
      yield {
        icon: 'support_agent',
        label: presenceTypes.map(p => translate('PRODUCT', 'presenceTypes', p) as string).join(', '),
      };
  })();

  return renderProductCardTags([...Array.from(tagsIterator)]);
});

const workshopSetCardSelectionIndicator = stateful<WorkshopSetCardData>(({ colorDef, selected, isClickable }) => {
  return workshopCardParts.workshopCardSelectionIndicator({
    isBlockedByOtherSelection: false,
    bookedAlready: false,
    selected,
    colorDef,
    isClickable,
  } as any);
});

export const workshopSetCardParts = {
  description: workshopSetCardDescription,
  workshops: workshopSetCardWorkshops,
  tags: workshopSetCardTags,
  indicator: workshopSetCardSelectionIndicator,
};
